import React from 'react';
import find from 'lodash/find';
import isNil from 'lodash/isNil';
import { useSelector } from 'react-redux';

import SidekickModuleSpec, { ModifierSpec, TypeSpec } from 'src/models/Sidekick/SidekickModuleSpec';
import { ModifierValue, NextData } from 'src/models/Sidekick/SidekickThreads';
import {
  selectAnonModifierIndex,
  selectCurrentAnonModule,
  selectCurrentMemories,
  selectCurrentModule,
  selectExtActionMemNames,
  selectModifierIndex,
  selectModuleSpecsMap,
  selectTypeSpecs,
} from 'src/presentation/components/pages/sidekick/threads/actions/threadsUiSlice';
import { getModuleSpec } from '../../utils/utils';

export interface ThreadModuleContextProps {
  modifier?: ModifierValue;
  modifierIndex?: number;
  modifierSpec?: ModifierSpec;
  module?: NextData;
  moduleSpec?: ModifierSpec;
  definedMemories: Record<string, string>;

  extActionMems: string[];
  typeSpecs: Record<string, TypeSpec>;
  isAnon: boolean;
  isModifier: boolean;
  moduleSpecsMap: Record<string, SidekickModuleSpec>;
  readOnly: boolean;
}

export const ThreadModuleContext = React.createContext<ThreadModuleContextProps>({
  definedMemories: {},
  typeSpecs: {},
  extActionMems: [],
  isAnon: false,
  isModifier: false,
  moduleSpecsMap: {},
  readOnly: false,
});

const ThreadModuleProvider = ({ children, readOnly, isAnonymousModule }) => {
  let module = useSelector(selectCurrentModule) as NextData;
  const modifierIndex = useSelector(selectModifierIndex);

  const anon = isAnonymousModule;
  const anonModule = useSelector(selectCurrentAnonModule);
  const anonModifierIndex = useSelector(selectAnonModifierIndex);

  const moduleSpecsMap = useSelector(selectModuleSpecsMap);
  const definedMemories = useSelector(selectCurrentMemories);
  const typeSpecs = useSelector(selectTypeSpecs);
  const extActionMems = useSelector(selectExtActionMemNames);

  if (!module) {
    return null;
  }

  let modifier = module?.modifierValues[modifierIndex];

  let moduleSpec = getModuleSpec(module, moduleSpecsMap) as SidekickModuleSpec | undefined;
  let isModifier = modifierIndex !== null;

  if (anon && anonModule) {
    moduleSpec = moduleSpecsMap[anonModule.name];
    module = anonModule;

    if (!isNil(anonModifierIndex)) {
      modifier = module.modifierValues[anonModifierIndex];
      isModifier = !!modifier;
    } else {
      isModifier = false;
    }
  }

  const modifierSpec = find(moduleSpec?.modifiers, spec => spec.name === modifier?.name) as ModifierSpec | undefined;

  const contextValue: ThreadModuleContextProps = {
    moduleSpecsMap,
    module: module,
    modifierIndex,
    moduleSpec,
    modifierSpec,
    definedMemories: definedMemories,
    typeSpecs,
    extActionMems,
    isAnon: anon,
    isModifier,
    modifier,
    readOnly,
  };

  return <ThreadModuleContext.Provider value={contextValue}>{children}</ThreadModuleContext.Provider>;
};

export default ThreadModuleProvider;
